'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGiphyGifs } from '@/lib/api/giphy/hooks';
import { cn } from '@/lib/ui-kit/utils';
import { SearchShell } from './SearchShell';

interface GifPanelProps {
  onSelect: (id: string) => void;
}

// GIF tab of the unified picker: search box + 3-column masonry grid with
// infinite scroll (Giphy trending when empty, search otherwise).
export const GifPanel = ({ onSelect }: GifPanelProps) => {
  const t = useTranslations('gifPicker');
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  // A picked mood filters without touching the input text; it stays highlighted
  // until the user types (which takes over) or taps it again to clear.
  const [activeMood, setActiveMood] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  // Mood drives the search instantly; typed text is debounced.
  const term = activeMood ?? debounced;
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGiphyGifs(term);
  const gifs = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const handleChange = (value: string) => {
    setQuery(value);
    if (activeMood) setActiveMood(null);
  };
  const selectMood = (mood: string) => {
    setActiveMood((prev) => (prev === mood ? null : mood));
    setQuery('');
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Only auto-page after the user has actually scrolled. Otherwise a short
  // first page (e.g. landscape GIFs that don't fill the viewport) keeps the
  // bottom sentinel in view and cascades into many pages the moment the
  // picker opens. Gating on a real scroll keeps the initial load to one page.
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollRef.current;
    if (!el || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasScrolledRef.current &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
      { root },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex h-[325px] flex-col">
      <SearchShell
        showMoods={!focused && query.length === 0}
        activeMood={activeMood}
        onMood={selectMood}
        onSearchIconClick={() => inputRef.current?.focus()}
        onBack={() => setActiveMood(null)}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={t('search')}
          className={cn(
            'min-w-[52px] flex-1 bg-transparent text-[13px] text-v2-text-primary outline-none placeholder:text-v2-text-secondary',
            // While a mood is active the input yields the row to the arrow + moods (per design).
            activeMood && 'hidden',
          )}
        />
      </SearchShell>
      <div
        ref={scrollRef}
        onScroll={() => {
          hasScrolledRef.current = true;
        }}
        className="mt-3 flex-1 overflow-y-auto"
      >
        {isError ? (
          <p className="py-8 text-center text-[13px] text-v2-text-secondary">{t('error')}</p>
        ) : isLoading ? (
          <p className="py-8 text-center text-[13px] text-v2-text-secondary">{t('trending')}…</p>
        ) : gifs.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-v2-text-secondary">{t('noResults')}</p>
        ) : (
          <>
            <div className="columns-3 gap-2">
              {gifs.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onSelect(g.id)}
                  className="mb-2 block w-full cursor-pointer overflow-hidden rounded-[6px]"
                >
                  <img src={g.previewUrl} alt={g.title} loading="lazy" className="w-full" />
                </button>
              ))}
            </div>
            {/* True bottom of the list — outside the masonry columns so it only
                comes into view when actually scrolled to the end. */}
            <div ref={sentinelRef} className="h-px w-full" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-2">
                <Loader2 className="size-4 animate-spin text-v2-text-secondary" />
              </div>
            )}
          </>
        )}
      </div>
      <div className="mt-2 shrink-0 text-right text-[10px] text-v2-text-secondary">
        Powered by GIPHY
      </div>
    </div>
  );
};
